import { useEffect, useMemo, useState } from "react";
import { Truck, Plus, Trash2, Inbox, PackageCheck, Wallet, FileText, Printer } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ProcurementAPI, AdminAPI } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/dummyData";
import { printHtml } from "@/lib/download";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const STATUS = {
  ISSUED: { label: "Diterbitkan", cls: "bg-amber-warm/20 text-amber-700" },
  RECEIVED: { label: "Barang Diterima", cls: "bg-sage/20 text-forest" },
  PAID: { label: "Sudah Dibayar", cls: "bg-forest/10 text-forest" },
  CANCELLED: { label: "Dibatalkan", cls: "bg-terracotta/15 text-terracotta" },
};
const ISSUER_ROLES = ["SUPER_ADMIN", "ADMIN_PUSAT", "ADMIN_WILAYAH", "ADMIN_DAERAH", "ADMIN_CABANG"];
const emptyItem = () => ({ item_name: "", quantity: "", unit: "", unit_price: "" });

export default function AdminProcurement() {
  const { user } = useAuth();
  const [pos, setPos] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [shops, setShops] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const canIssue = ISSUER_ROLES.includes(user?.role);

  const load = () => ProcurementAPI.list().then((r) => setPos(r || [])).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => {
    load();
    if (ISSUER_ROLES.includes(user?.role)) {
      AdminAPI.organizationsManage().then(setOrgs).catch(() => {});
      // Toko/gerai milik anggota yang sudah aktif — tujuan pengiriman di tingkat anggota.
      AdminAPI.shops("APPROVED").then((r) => setShops(r || [])).catch(() => {});
    }
  }, []); // eslint-disable-line

  // --- Terbitkan Surat Pesanan ---
  const [addOpen, setAddOpen] = useState(false);
  // destination = "org:<id>" (kantor/unit) atau "shop:<id>" (toko/gerai anggota).
  const [form, setForm] = useState({ supplier_name: "", supplier_contact: "", destination: "", note: "" });
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  // Tujuan pengiriman = unit di BAWAH kantor (mis. Ranting/Gerai) dalam lingkup.
  const destinationOptions = useMemo(() => orgs.filter((o) => ["KANTOR_CABANG", "KANTOR_RANTING", "UNIT_GERAI_OUTLET"].includes(o.level)), [orgs]);
  const total = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const submitPO = async () => {
    if (!form.supplier_name || !form.destination) return toast.error("Isi supplier & unit tujuan");
    const valid = items.filter((it) => it.item_name && Number(it.quantity) > 0);
    if (valid.length === 0) return toast.error("Tambahkan minimal 1 item");
    const [kind, id] = form.destination.split(":");
    const payload = {
      supplier_name: form.supplier_name,
      supplier_contact: form.supplier_contact,
      note: form.note,
      items: valid,
      ...(kind === "shop" ? { destination_shop_id: Number(id) } : { destination_org_id: Number(id) }),
    };
    setSaving(true);
    try {
      await ProcurementAPI.create(payload);
      toast.success("Surat pesanan diterbitkan");
      setAddOpen(false); setForm({ supplier_name: "", supplier_contact: "", destination: "", note: "" }); setItems([emptyItem()]);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Gagal menerbitkan"); }
    finally { setSaving(false); }
  };

  // --- Aksi penerimaan & pembayaran ---
  const [act, setAct] = useState(null); // { po, mode: 'receive'|'pay' }
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const openAct = (po, mode) => { setAct({ po, mode }); setNote(""); setAmount(String(po.total_amount || "")); };
  const doAct = async () => {
    setBusy(true);
    try {
      if (act.mode === "receive") { await ProcurementAPI.receive(act.po.id, { note }); toast.success("Bukti penerimaan barang dibuat"); }
      else { await ProcurementAPI.pay(act.po.id, { amount: Number(amount), note }); toast.success("Pembayaran dicatat"); }
      setAct(null); load();
    } catch (err) { toast.error(err?.response?.data?.error || "Gagal memproses"); }
    finally { setBusy(false); }
  };

  const printPO = async (poRow) => {
    try {
      const po = await ProcurementAPI.get(poRow.id);
      const rows = (po.items || []).map((i) => `<tr><td>${i.item_name}</td><td class="right">${i.quantity} ${i.unit || ""}</td><td class="right">${formatRupiah(i.unit_price)}</td><td class="right">${formatRupiah(i.line_total)}</td></tr>`).join("");
      // Cap/watermark sesuai status dokumen (mis. "LUNAS" saat pembayaran ke supplier terverifikasi).
      const WM = {
        PAID: { text: "Lunas", color: "#1f5136" },
        RECEIVED: { text: "Diterima", color: "#8a6d1f" },
        CANCELLED: { text: "Dibatalkan", color: "#a23b2f" },
      };
      const pay = po.payment;
      const payInfo = pay
        ? `<table><tr><td><b>Dibayar oleh</b></td><td>${pay.payer_name || "-"} · ${formatDate(pay.paid_at)}</td></tr>
             <tr><td><b>Metode</b></td><td>${pay.method || "-"}</td></tr>
             <tr><td><b>Jumlah Dibayar</b></td><td>${formatRupiah(pay.amount)}</td></tr></table>`
        : "";
      printHtml(`Surat Pesanan ${po.po_number}`, `<h1>SURAT PESANAN</h1><div class="muted">No. ${po.po_number} · ${new Date(po.created_at).toLocaleString("id-ID")}</div>
        <table><tr><td><b>Penerbit</b></td><td>${po.issuer_org_name || "-"}</td></tr>
        <tr><td><b>Supplier</b></td><td>${po.supplier_name}</td></tr>
        <tr><td><b>Kirim ke</b></td><td>${po.destination_shop_name || po.destination_org_name || "-"}</td></tr>
        <tr><td><b>Status</b></td><td>${STATUS[po.status]?.label || po.status}</td></tr></table>
        <table><thead><tr><th>Item</th><th class="right">Qty</th><th class="right">Harga</th><th class="right">Subtotal</th></tr></thead><tbody>${rows}</tbody></table>
        <p class="right"><b>Total: ${formatRupiah(po.total_amount)}</b></p>${po.note ? `<p class="muted">Catatan: ${po.note}</p>` : ""}${payInfo}`,
        { watermark: WM[po.status] });
    } catch { toast.error("Gagal memuat surat pesanan"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Pengadaan Barang</h1>
          <p className="text-ink/75 mt-2 text-sm">Surat Pesanan → Penerimaan Barang → Pembayaran, antar level kantor.</p>
        </div>
        {canIssue && (
          <Button className="rounded-lg bg-forest hover:bg-forest-dark text-white" onClick={() => setAddOpen(true)} data-testid="po-add">
            <Plus size={15} /> Terbitkan Surat Pesanan
          </Button>
        )}
      </div>

      <Card className="rounded-xl border-stone-soft p-6">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-stone-soft bg-cream/40 hover:bg-cream/40">
                <TableHead className="text-[10px] uppercase tracking-wider">No. Surat</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Supplier</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Penerbit</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Kirim ke</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Total</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pos.map((po) => (
                <TableRow key={po.id} className="border-stone-soft hover:bg-cream/30" data-testid={`po-row-${po.id}`}>
                  <TableCell className="font-mono text-xs font-semibold text-ink">{po.po_number}</TableCell>
                  <TableCell className="text-sm">{po.supplier_name}</TableCell>
                  <TableCell className="text-sm text-ink/75">{po.issuer_org_name || "-"}</TableCell>
                  <TableCell className="text-sm text-ink/75">{po.destination_shop_name || po.destination_org_name || "-"}</TableCell>
                  <TableCell className="text-right font-semibold">{formatRupiah(po.total_amount)}</TableCell>
                  <TableCell><Badge className={`${STATUS[po.status]?.cls} border-0 rounded-full text-[10px] uppercase tracking-wider`}>{STATUS[po.status]?.label || po.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-cream" title="Cetak" onClick={() => printPO(po)}><Printer size={14} /></Button>
                      {po.can_receive && <Button size="sm" className="rounded-lg bg-forest hover:bg-forest-dark text-white h-8" onClick={() => openAct(po, "receive")} data-testid={`po-receive-${po.id}`}><PackageCheck size={13} /> Terima</Button>}
                      {po.can_pay && <Button size="sm" className="rounded-lg bg-amber-warm text-ink hover:bg-amber-warm/90 h-8" onClick={() => openAct(po, "pay")} data-testid={`po-pay-${po.id}`}><Wallet size={13} /> Bayar</Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {loaded && pos.length === 0 && (
            <div className="py-14 text-center"><Inbox className="mx-auto text-ink/30" size={30} /><p className="text-sm text-ink/55 mt-2">Belum ada surat pesanan</p></div>
          )}
        </div>
      </Card>

      {/* Dialog Terbitkan Surat Pesanan */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-2xl max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Terbitkan Surat Pesanan</DialogTitle>
            <DialogDescription>Pesan barang ke supplier, tentukan unit tujuan pengiriman.</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Nama Supplier</Label><Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="cth. Supplier Ayam Segar" className="rounded-lg" data-testid="po-supplier" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Kontak Supplier (opsional)</Label><Input value={form.supplier_contact} onChange={(e) => setForm({ ...form, supplier_contact: e.target.value })} placeholder="No. HP / PIC" className="rounded-lg" /></div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/55">Kirim ke Unit Tujuan</Label>
              <Select value={form.destination} onValueChange={(v) => setForm({ ...form, destination: v })}>
                <SelectTrigger className="rounded-lg" data-testid="po-destination"><SelectValue placeholder="Pilih unit / gerai tujuan..." /></SelectTrigger>
                <SelectContent>
                  {destinationOptions.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-ink/45 font-semibold">Kantor / Unit</div>
                      {destinationOptions.map((o) => <SelectItem key={`org-${o.id}`} value={`org:${o.id}`}>{o.name}</SelectItem>)}
                    </>
                  )}
                  {shops.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-ink/45 font-semibold border-t border-stone-soft mt-1 pt-2">Gerai / Toko Anggota</div>
                      {shops.map((s) => <SelectItem key={`shop-${s.id}`} value={`shop:${s.id}`}>{s.shop_name}{s.owner_name ? ` — ${s.owner_name}` : ""}</SelectItem>)}
                    </>
                  )}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-ink/45">Pilih kantor/unit koperasi, atau kirim langsung ke gerai/toko milik anggota yang membutuhkan bahan baku.</p>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider text-ink/55">Item Pesanan</Label>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setItems((a) => [...a, emptyItem()])}><Plus size={13} /> Item</Button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input value={it.item_name} onChange={(e) => setItem(i, "item_name", e.target.value)} placeholder="Nama barang" className="col-span-4 rounded-lg" />
                  <Input type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} placeholder="Qty" className="col-span-2 rounded-lg" />
                  <Input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} placeholder="satuan" className="col-span-2 rounded-lg" />
                  <Input type="number" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} placeholder="Harga" className="col-span-3 rounded-lg" />
                  <button onClick={() => setItems((a) => a.filter((_, idx) => idx !== i))} className="col-span-1 grid place-items-center text-ink/40 hover:text-terracotta"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <div className="text-right mt-2 font-semibold text-ink">Total: {formatRupiah(total)}</div>
          </div>

          <div className="space-y-1.5 mt-2"><Label className="text-xs uppercase tracking-wider text-ink/55">Catatan (opsional)</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="rounded-lg" /></div>

          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAddOpen(false)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={submitPO} disabled={saving} data-testid="po-submit">
              {saving ? "Menerbitkan…" : "Terbitkan Surat Pesanan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Penerimaan / Pembayaran */}
      <Dialog open={!!act} onOpenChange={(v) => !v && setAct(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">{act?.mode === "receive" ? "Bukti Penerimaan Barang" : "Bukti Pembayaran"}</DialogTitle>
            <DialogDescription>{act ? `${act.po.po_number} · ${act.po.supplier_name}` : ""}</DialogDescription>
          </DialogHeader>
          {act?.mode === "pay" && (
            <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink/55">Jumlah Dibayar</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-lg" data-testid="po-pay-amount" /></div>
          )}
          <div className="space-y-1.5 mt-2"><Label className="text-xs uppercase tracking-wider text-ink/55">Catatan (opsional)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-lg" placeholder={act?.mode === "receive" ? "cth. Barang diterima lengkap & sesuai" : "cth. Ditransfer via BCA"} /></div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAct(null)}>Batal</Button>
            <Button className="rounded-full bg-forest hover:bg-forest-dark text-white" onClick={doAct} disabled={busy} data-testid="po-act-submit">
              {busy ? "Memproses…" : act?.mode === "receive" ? "Konfirmasi Terima" : "Catat Pembayaran"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
